import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Stack } from '@mui/material';
import React, { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';

import { ExhibitionFormFields } from '@app/components/Order/ExhibitionFormFields';
import { InquiryStepTwoState } from '@app/domain/order';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { useGADataLayer } from '@app/hooks/useGADataLayer';
import { GA_CUSTOM_EVENT } from '@app/static/constants';
import { theme } from '@app/theme';
import { InquiryStepTwoFormData } from '@app/types/order';
import {
  InquiryStepTwoQuestion,
  InquiryType,
  OrganizationCustomFields,
} from '@app/types/organization';

interface InquiryStepTwoProps {
  defaultValues?: InquiryStepTwoFormData;
  inquiryType: InquiryType;
  onBack: () => void;
  onSubmit: (data: InquiryStepTwoFormData) => void;
  organizationCustomField?: OrganizationCustomFields;
}

export function InquiryStepTwo({
  onSubmit,
  onBack,
  defaultValues,
  organizationCustomField,
  inquiryType,
}: InquiryStepTwoProps): ReactElement {
  const [stepData, setStepData] = useRecoilState(InquiryStepTwoState);
  const { isMobile } = useDeviceType();
  const { sendGAEvent } = useGADataLayer();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
  } = useForm<InquiryStepTwoFormData>({
    defaultValues: defaultValues || stepData,
  });

  const handleBack = async () => {
    const currentData = getValues();
    await setStepData((prevData: InquiryStepTwoFormData) => ({
      ...prevData,
      ...currentData,
    }));
    onBack();
  };

  // inquiryTypeに応じて参照する値を切り替え
  let questions: InquiryStepTwoQuestion[] = [];
  switch (inquiryType) {
    case InquiryType.ExhibitionVisitor:
      questions =
        organizationCustomField?.exhibitionVisitorReserveQuestions || [];
      break;
    case InquiryType.ExhibitionRequestDoc:
      questions = organizationCustomField?.exhibitionRequestDocQuestions || [];
      break;
    case InquiryType.PropertyVisitor:
      questions =
        organizationCustomField?.propertyVisitorReserveQuestions || [];
      break;
    case InquiryType.PropertyRequestDoc:
      questions = organizationCustomField?.propertyRequestDocQuestions || [];
      break;
    default:
      questions = [];
  }

  return (
    <Stack
      sx={{
        alignItems: 'center',
        width: '100%',
      }}
      spacing={4}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit((data) => {
          setStepData((prevData: InquiryStepTwoFormData) => ({
            ...prevData,
            ...data,
          }));
          onSubmit(data);
          sendGAEvent(GA_CUSTOM_EVENT.INQUIRY_STEP2, isMobile);
        })}
        sx={{
          backgroundColor: theme.palette.neutral.white,
          borderRadius: '8px',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <ExhibitionFormFields
          organizationCustomField={organizationCustomField}
          questions={questions}
          control={control}
          errors={errors}
          isDisabled={false}
          setValue={setValue}
        />
        <Stack mb={2} mt={6} width="100%">
          <Stack
            alignItems="center"
            direction={{
              md: 'row',
              sm: 'row',
              xs: 'column-reverse',
            }}
            spacing={1}
            justifyContent="center"
            width="100%"
          >
            <Button
              type="button"
              startIcon={<ArrowBackIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.blueAccent.main,
                  color: '#ffffff',
                },
                alignItems: 'center',
                backgroundColor: '#ffffff',
                border: `1px solid ${theme.palette.blueAccent.main}`,
                borderRadius: 10,
                color: theme.palette.blueAccent.main,
                display: 'flex',
                justifyContent: 'space-between',
                pl: 2,
                pr: { sm: 13, xs: 'calc(40vw - 20px)' },
                textTransform: 'none',
                width: { md: 250, sm: 250, xs: '80vw' },
              }}
              onClick={handleBack}
            >
              戻る
            </Button>
            <Button
              endIcon={<ArrowForwardIcon />}
              type="submit"
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: '#d6e7f9',
                  color: '#ffffff',
                },
                '&:hover': {
                  backgroundColor: theme.palette.blueAccent.main,
                  color: '#ffffff',
                },
                alignItems: 'center',
                backgroundColor: theme.palette.blueAccent.main,
                borderRadius: 10,
                color: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                pl: { sm: 13, xs: 'calc(40vw - 20px)' },
                pr: 2,
                textTransform: 'none',
                width: { md: 250, sm: 250, xs: '80vw' },
              }}
              disabled={!isValid}
            >
              次へ
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
